/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 *                        ⚖️ HEARTBEATS™ - DOCUMENTS LÉGAUX
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Copyright (c) 2024-2026 Heartbeats™ - Tous droits réservés
 * 
 * PROPRIÉTAIRE EXCLUSIF: Heartbeats
 * Adresse: 141 Rue Arthur Lamendin, 62980 Vermelles, France
 * 
 * PROPRIÉTÉ INTELLECTUELLE:
 * L'ensemble du code source, des algorithmes, des designs, des personnages
 * virtuels (Luna™, Enzo™, Pixie™, Nyx™, Kurson™), des fonctionnalités
 * (Soul™, Shadow Mode™, Blind Date™, HBT Tokens™) et de tout autre élément
 * constituant l'application Heartbeats™ est la propriété exclusive et 
 * inaliénable de Heartbeats.
 * 
 * Toute reproduction, modification, distribution, rétro-ingénierie ou 
 * utilisation non autorisée est strictement interdite et passible de 
 * poursuites judiciaires conformément aux articles L335-2 et suivants 
 * du Code de la Propriété Intellectuelle français.
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Lock, Heart, AlertTriangle, Users, Mail, Scale, Database, Eye, FileText } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// 📜 MODAL CGU HEARTBEATS
// ═══════════════════════════════════════════════════════════════════════════════

export const HeartbeatsCGUModal = ({ isOpen, onClose, isDarkMode = true }) => {
  if (!isOpen) return null;

  const sections = [
    {
      title: "1. Objet et acceptation",
      content: `Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation de l'application Heartbeats™, service de mise en relation et de rencontres de proximité édité par Heartbeats.

En utilisant Heartbeats™, vous acceptez sans réserve les présentes CGU. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser l'application.`
    },
    {
      title: "2. Propriété intellectuelle",
      icon: <Scale className="w-5 h-5" />,
      content: `⚠️ PROPRIÉTÉ EXCLUSIVE - TOUS DROITS RÉSERVÉS

**Heartbeats** est le propriétaire exclusif et inaliénable de l'ensemble des éléments constituant l'application Heartbeats™, notamment :

📌 **Code source et algorithmes** :
- Algorithme Soul™ (compatibilité et matching)
- Algorithme Psyche™ (analyse comportementale)
- Système Destins Croisés™ (croisements géolocalisés)
- Tous les scripts, API et logiques métier

📌 **Personnages virtuels et IA** :
- Luna™ (guide féminin)
- Enzo™ (guide masculin)
- Pixie™ (guide ludique)
- Nyx™ (guide Shadow Mode)
- Kurson™ (guide mystique)

📌 **Fonctionnalités propriétaires** :
- Shadow Mode™ (mode anonyme)
- Blind Date™ (rencontres à l'aveugle)
- HBT Tokens™ (économie virtuelle)
- Super Like™, Crush Secret™

📌 **Design et interface** :
- Charte graphique, logos, icônes
- Animations et effets visuels
- Architecture UX/UI

Toute reproduction, copie, modification, adaptation, traduction, représentation, diffusion, commercialisation, rétro-ingénierie, décompilation ou utilisation non autorisée de tout ou partie de ces éléments est STRICTEMENT INTERDITE.

Les contrevenants s'exposent à des poursuites judiciaires conformément aux articles L335-2 et suivants du Code de la Propriété Intellectuelle français, avec des peines pouvant aller jusqu'à 3 ans d'emprisonnement et 300 000€ d'amende.`
    },
    {
      title: "3. Conditions d'accès",
      icon: <Users className="w-5 h-5" />,
      content: `**Âge minimum** : L'utilisation de Heartbeats™ est STRICTEMENT réservée aux personnes majeures (18 ans et plus). En vous inscrivant, vous certifiez sur l'honneur avoir au moins 18 ans.

**Vérification** : Nous nous réservons le droit de demander une vérification d'identité à tout moment.

**Inscription** : L'accès au service nécessite la création d'un compte avec des informations exactes et à jour. Vous êtes responsable de la confidentialité de vos identifiants.

**Un seul compte** : Chaque utilisateur ne peut posséder qu'un seul compte. La création de comptes multiples entraîne la suppression immédiate de tous les comptes.`
    },
    {
      title: "4. Description du service",
      content: `Heartbeats™ propose :
- Un service de mise en relation entre utilisateurs majeurs
- Un système de matching basé sur la compatibilité (algorithme Soul™)
- Des croisements géolocalisés (Destins Croisés™)
- Des salons de discussion vocaux et écrits
- Un mode anonyme (Shadow Mode™)
- Un système de tokens virtuels (HBT™)
- Un programme d'affiliation

Le service est fourni "en l'état". Nous nous réservons le droit de modifier, suspendre ou interrompre tout ou partie du service à tout moment, sans préavis ni indemnité.`
    },
    {
      title: "5. Comportement des utilisateurs",
      icon: <AlertTriangle className="w-5 h-5" />,
      content: `**Sont STRICTEMENT INTERDITS** :
❌ Tout contenu illégal, diffamatoire, obscène, menaçant ou harcelant
❌ L'usurpation d'identité ou la création de faux profils
❌ Le harcèlement sous toute forme (sexuel, moral, cyber)
❌ La sollicitation commerciale non autorisée
❌ Le partage de contenus à caractère sexuel explicite non sollicités
❌ Tout comportement portant atteinte aux autres utilisateurs
❌ L'utilisation de bots, scripts ou automatisations
❌ La tentative de contournement des mesures de sécurité

**Modération** : Nous nous réservons le droit de suspendre ou supprimer IMMÉDIATEMENT tout compte ne respectant pas ces règles, sans préavis ni remboursement. Les cas graves seront signalés aux autorités compétentes.`
    },
    {
      title: "6. Programme d'affiliation",
      content: `Le programme d'affiliation Heartbeats™ permet de percevoir des commissions sur les abonnements souscrits par vos filleuls.

**Commissions** : N1=8%, N2=5%, N3=2%, N4=1%

**Conditions** :
- Les commissions sont calculées sur les abonnements Premium effectivement payés
- Le retrait minimum est de 20€
- Les revenus sont soumis à déclaration fiscale (vous êtes responsable de vos obligations fiscales)
- Ce n'est PAS un système pyramidal : les revenus proviennent uniquement des ventes réelles

**Résiliation** : Heartbeats se réserve le droit de modifier ou mettre fin au programme d'affiliation à tout moment.`
    },
    {
      title: "7. Responsabilité",
      content: `**Notre responsabilité** : Nous mettons tout en œuvre pour assurer le bon fonctionnement et la sécurité du service mais ne pouvons garantir une disponibilité permanente ni l'absence totale de bugs.

**Votre responsabilité** : Vous êtes seul responsable de vos interactions avec les autres utilisateurs. Heartbeats™ n'est pas responsable des comportements des utilisateurs entre eux.

**Rencontres physiques** : Les rencontres physiques se font ENTIÈREMENT à vos risques et périls. Nous vous recommandons la plus grande prudence et de toujours informer un proche.

**Limitation** : En aucun cas Heartbeats ne pourra être tenu responsable des dommages indirects, perte de données, perte de chance, préjudice moral ou financier résultant de l'utilisation du service.`
    },
    {
      title: "8. Résiliation",
      content: `**Par l'utilisateur** : Vous pouvez supprimer votre compte à tout moment depuis les paramètres de l'application (Droit à l'oubli RGPD).

**Par Heartbeats** : Nous pouvons résilier votre compte immédiatement et sans préavis en cas de :
- Violation des présentes CGU
- Comportement frauduleux ou nuisible
- Non-paiement des services Premium

**Conséquences** : En cas de résiliation pour faute, aucun remboursement ne sera effectué. Les données seront supprimées conformément à notre Politique de Confidentialité.`
    },
    {
      title: "9. Modification des CGU",
      content: `Nous nous réservons le droit de modifier les présentes CGU à tout moment. Les utilisateurs seront informés des modifications substantielles par notification dans l'application.

La continuation de l'utilisation du service après modification vaut acceptation des nouvelles CGU.

Si vous n'acceptez pas les nouvelles conditions, vous devez cesser d'utiliser le service et supprimer votre compte.`
    },
    {
      title: "10. Droit applicable et juridiction",
      content: `Les présentes CGU sont soumises au droit français.

**Médiation** : En cas de litige, une solution amiable sera recherchée en priorité. Vous pouvez recourir gratuitement au service de médiation de la consommation.

**Juridiction** : À défaut de résolution amiable, les tribunaux français seront seuls compétents, et plus particulièrement le Tribunal de Commerce de Béthune.`
    },
    {
      title: "11. Contact",
      icon: <Mail className="w-5 h-5" />,
      content: `Pour toute question concernant ces CGU :

**Heartbeats**
141 Rue Arthur Lamendin
62980 Vermelles
France

Email : contact@heartbeats.fr
Téléphone : 07 80 64 89 76

Dernière mise à jour : Janvier 2026`
    }
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className={`relative w-full max-w-3xl max-h-[85vh] overflow-x-hidden overflow-y-auto rounded-2xl ${
            isDarkMode ? 'bg-gray-900' : 'bg-white'
          } shadow-2xl`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`sticky top-0 z-10 px-6 py-4 border-b ${
            isDarkMode ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'
          } backdrop-blur-sm`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Conditions Générales d'Utilisation
                  </h2>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Heartbeats™ - Version Janvier 2026
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(85vh-80px)] p-6">
            {/* Intro */}
            <div className={`p-4 rounded-xl mb-6 ${
              isDarkMode ? 'bg-purple-900/30 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'
            }`}>
              <div className="flex items-center gap-3 mb-2">
                <Heart className="w-6 h-6 text-pink-400" />
                <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Heartbeats™
                </span>
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Bienvenue sur Heartbeats™ ! Ces conditions régissent votre utilisation de notre service de rencontres. 
                Merci de les lire attentivement avant de continuer.
              </p>
            </div>

            {/* Sections */}
            {sections.map((section, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className={`mb-4 p-4 rounded-xl ${
                  isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'
                } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
              >
                <h3 className={`font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {section.icon && <span className="text-purple-400">{section.icon}</span>}
                  {section.title}
                </h3>
                <div className={`text-sm whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {section.content}
                </div>
              </motion.div>
            ))}

            {/* Footer */}
            <div className={`text-center pt-4 pb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <p className="text-xs">© 2026 Heartbeats™ - Tous droits réservés</p>
              <p className="text-xs mt-1">Propriété exclusive et inaliénable</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🔒 MODAL POLITIQUE DE CONFIDENTIALITÉ HEARTBEATS
// ═══════════════════════════════════════════════════════════════════════════════

export const HeartbeatsPrivacyModal = ({ isOpen, onClose, isDarkMode = true }) => {
  if (!isOpen) return null;

  const sections = [
    {
      title: "1. Responsable du traitement",
      icon: <Shield className="w-5 h-5" />,
      content: `**Heartbeats**
141 Rue Arthur Lamendin
62980 Vermelles, France

Email : contact@heartbeats.fr
Téléphone : 07 80 64 89 76

Nous sommes responsables du traitement de vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD - UE 2016/679).`
    },
    {
      title: "2. Données collectées",
      icon: <Database className="w-5 h-5" />,
      content: `**Données d'identification** :
- Prénom, pseudo, avatar
- Adresse email
- Numéro de téléphone (optionnel)
- Date de naissance (vérification de majorité)

**Données de profil** :
- Photos et avatar personnalisé
- Bio et centres d'intérêts
- Localisation approximative (ville)
- Préférences de rencontre
- Statut relationnel

**Données sensibles (Article 9 RGPD)** :
- Orientation sexuelle (pour le matching)
- Photos (si vérification selfie activée)

⚠️ Ces données sensibles ne sont collectées QU'AVEC votre consentement explicite et peuvent être retirées à tout moment.

**Données techniques** :
- Adresse IP (anonymisée après 30 jours)
- Type d'appareil et navigateur
- Données de connexion et d'utilisation
- Logs d'activité (anonymisés)`
    },
    {
      title: "3. Finalités du traitement",
      content: `Vos données sont utilisées pour :

✅ **Fournir le service** : Création de compte, matching Soul™, messagerie, croisements
✅ **Améliorer l'expérience** : Personnalisation, recommandations intelligentes
✅ **Sécurité** : Détection de fraudes, modération, vérification de profils
✅ **Communication** : Notifications, informations sur le service
✅ **Économie HBT** : Gestion des tokens, récompenses, programme d'affiliation
✅ **Obligations légales** : Conservation des données requises par la loi

❌ Vos données ne sont JAMAIS vendues à des tiers.
❌ Aucun profilage publicitaire n'est effectué.`
    },
    {
      title: "4. Base légale",
      content: `Nos traitements reposent sur :

📌 **Votre consentement** (Article 6.1.a RGPD) :
- Données sensibles (orientation, photos)
- Cookies non essentiels
- Notifications marketing

📌 **L'exécution du contrat** (Article 6.1.b RGPD) :
- Fourniture du service de matching
- Gestion du compte et des abonnements

📌 **Notre intérêt légitime** (Article 6.1.f RGPD) :
- Sécurité et prévention des fraudes
- Amélioration continue du service

📌 **Obligations légales** (Article 6.1.c RGPD) :
- Conservation des données de facturation
- Réponse aux réquisitions judiciaires`
    },
    {
      title: "5. Durée de conservation",
      content: `⏱️ **Compte actif** : Données conservées tant que le compte existe

🗑️ **Compte supprimé** : 
- Données personnelles : supprimées sous 30 jours
- Messages : anonymisés immédiatement
- Photos : supprimées sous 7 jours

📸 **Données de vérification** : Supprimées immédiatement après validation

📊 **Logs techniques** : 12 mois maximum (anonymisés)

💳 **Données de facturation** : 10 ans (obligation légale française)

🔒 **Données de modération** : Conservées 3 ans en cas de signalement grave`
    },
    {
      title: "6. Vos droits RGPD",
      icon: <Eye className="w-5 h-5" />,
      content: `Conformément au RGPD, vous disposez des droits suivants :

📋 **Droit d'accès** (Art. 15) : Obtenir une copie de vos données
✏️ **Droit de rectification** (Art. 16) : Corriger vos données inexactes
🗑️ **Droit à l'effacement** (Art. 17) : Supprimer vos données ("droit à l'oubli")
⏸️ **Droit à la limitation** (Art. 18) : Suspendre le traitement
📦 **Droit à la portabilité** (Art. 20) : Récupérer vos données en format standard
🚫 **Droit d'opposition** (Art. 21) : Vous opposer à certains traitements
↩️ **Droit de retrait du consentement** : À tout moment, sans justification

**Pour exercer vos droits** :
- Depuis l'application : Paramètres > Mes données > Exercer mes droits
- Par email : contact@heartbeats.fr

⏰ Délai de réponse : 30 jours maximum (prolongeable de 60 jours si complexe)`
    },
    {
      title: "7. Sécurité des données",
      icon: <Lock className="w-5 h-5" />,
      content: `Nous mettons en œuvre des mesures de sécurité strictes :

🔐 **Chiffrement** : Données sensibles chiffrées (AES-256)
🔒 **Connexions sécurisées** : HTTPS/TLS 1.3 obligatoire
🛡️ **Protection accès** : Authentification forte, 2FA disponible
📊 **Moindre privilège** : Accès aux données limité au strict nécessaire
🔍 **Surveillance** : Détection d'intrusions et alertes en temps réel
🏛️ **Hébergement** : Serveurs sécurisés en Union Européenne

**En cas de violation de données** :
Vous serez informé dans les 72 heures si un risque existe pour vos droits et libertés (Article 34 RGPD).`
    },
    {
      title: "8. Partage des données",
      content: `Vos données peuvent être partagées avec :

👥 **Autres utilisateurs** : Uniquement les informations de profil que vous rendez visibles

🔧 **Prestataires techniques** (sous-traitants RGPD) :
- Hébergement cloud (serveurs UE)
- Paiement sécurisé (données minimales)
- Envoi de notifications

⚖️ **Autorités** : Uniquement sur demande légale justifiée

🚫 **Aucun transfert hors UE** sans garanties appropriées (clauses contractuelles types ou décision d'adéquation).

🚫 **Aucune vente de données** à des tiers, jamais.`
    },
    {
      title: "9. Cookies",
      content: `Nous utilisons des cookies pour :

✅ **Essentiels** (toujours actifs) :
- Session et authentification
- Préférences de confidentialité
- Sécurité

⚙️ **Fonctionnels** (avec consentement) :
- Mémorisation des préférences
- Dernière position dans l'app

📊 **Analytiques** (avec consentement) :
- Statistiques d'utilisation anonymes
- Amélioration du service

Vous pouvez gérer vos préférences à tout moment dans les paramètres.`
    },
    {
      title: "10. Mineurs",
      content: `⚠️ **HEARTBEATS™ EST STRICTEMENT INTERDIT AUX MINEURS DE MOINS DE 18 ANS.**

Nous ne collectons pas sciemment de données de mineurs. Si nous découvrons qu'un mineur utilise le service :
- Son compte sera immédiatement supprimé
- Toutes ses données seront effacées
- Un signalement pourra être effectué si nécessaire

👨‍👩‍👧 **Parents** : Si vous pensez que votre enfant mineur utilise Heartbeats™, contactez-nous immédiatement à contact@heartbeats.fr`
    },
    {
      title: "11. Modifications",
      content: `Cette politique peut être modifiée pour refléter les évolutions légales ou de notre service.

📢 **Notification** : En cas de changement substantiel, vous serez informé par :
- Notification push dans l'application
- Email si modification majeure

📅 La date de dernière mise à jour est indiquée en bas de ce document.

Si vous n'acceptez pas les modifications, vous pouvez supprimer votre compte à tout moment.`
    },
    {
      title: "12. Réclamation",
      content: `Si vous estimez que vos droits ne sont pas respectés :

**1. Contactez-nous d'abord** :
contact@heartbeats.fr
Nous nous engageons à répondre sous 30 jours.

**2. Saisissez la CNIL** :
Commission Nationale de l'Informatique et des Libertés
3 Place de Fontenoy, TSA 80715
75334 Paris Cedex 07
www.cnil.fr

Vous pouvez déposer une réclamation en ligne sur le site de la CNIL.`
    }
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className={`relative w-full max-w-3xl max-h-[85vh] overflow-x-hidden overflow-y-auto rounded-2xl ${
            isDarkMode ? 'bg-gray-900' : 'bg-white'
          } shadow-2xl`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`sticky top-0 z-10 px-6 py-4 border-b ${
            isDarkMode ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'
          } backdrop-blur-sm`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Politique de Confidentialité
                  </h2>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Heartbeats™ - Conforme RGPD
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(85vh-80px)] p-6">
            {/* Intro */}
            <div className={`p-4 rounded-xl mb-6 ${
              isDarkMode ? 'bg-green-900/30 border border-green-500/30' : 'bg-green-50 border border-green-200'
            }`}>
              <div className="flex items-center gap-3 mb-2">
                <Heart className="w-6 h-6 text-pink-400" />
                <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Protection de vos données
                </span>
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Votre vie privée est notre priorité. Cette politique explique comment nous collectons, 
                utilisons et protégeons vos données personnelles conformément au RGPD.
              </p>
            </div>

            {/* Sections */}
            {sections.map((section, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className={`mb-4 p-4 rounded-xl ${
                  isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'
                } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
              >
                <h3 className={`font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {section.icon && <span className="text-green-400">{section.icon}</span>}
                  {section.title}
                </h3>
                <div className={`text-sm whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {section.content}
                </div>
              </motion.div>
            ))}

            {/* Footer */}
            <div className={`text-center pt-4 pb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <p className="text-xs">© 2026 Heartbeats™ - Tous droits réservés</p>
              <p className="text-xs mt-1">Dernière mise à jour : Janvier 2026</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🔗 COMPOSANT LIENS LÉGAUX DISCRETS (Footer du module)
// ═══════════════════════════════════════════════════════════════════════════════

export const HeartbeatsLegalLinks = ({ isDarkMode = true, onOpenCGU, onOpenPrivacy }) => {
  return (
    <div className={`flex items-center justify-center gap-4 py-3 text-xs ${
      isDarkMode ? 'text-gray-500' : 'text-gray-400'
    }`}>
      <button
        onClick={onOpenCGU}
        className={`flex items-center gap-1 hover:text-purple-400 transition-colors`}
        data-testid="heartbeats-cgu-link"
      >
        <FileText className="w-3 h-3" />
        CGU
      </button>
      <span>•</span>
      <button
        onClick={onOpenPrivacy}
        className={`flex items-center gap-1 hover:text-green-400 transition-colors`}
        data-testid="heartbeats-privacy-link"
      >
        <Lock className="w-3 h-3" />
        Confidentialité
      </button>
      <span>•</span>
      <span className="flex items-center gap-1">
        <Heart className="w-3 h-3 text-pink-400" />
        Heartbeats™ 2026
      </span>
    </div>
  );
};

// Named exports déjà définis au-dessus : HeartbeatsCGUModal, HeartbeatsPrivacyModal, HeartbeatsLegalLinks
